import './RunSelector.scss';
import { useMemo } from 'react';
import { MdRefresh } from 'react-icons/md';
import { Appearance, Placement, Size, StretchMode } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { IconButton } from '@Components/IconButton';

// Any run list: Data QA runs (keyed by S3 key), scaling runs (keyed by id).
interface RunSelectorProps<Run> {
  runs: Run[];
  /** A run's stable key, e.g. its S3 key or id. */
  runKey: (run: Run) => string;
  /** e.g. run-utils' `runLabel` for Data QA runs. */
  formatLabel: (run: Run) => string;
  selectedKey: string;
  onSelectRun: (key: string) => void;
  /** Re-list runs from S3. When provided, a refresh button sits beside the picker. */
  onRefresh?: () => void;
  /** Spins the refresh button while a refetch is in flight. */
  isRefreshing?: boolean;
}

export const RunSelector = <Run,>({
  runs,
  runKey,
  formatLabel,
  selectedKey,
  onSelectRun,
  onRefresh,
  isRefreshing = false
}: RunSelectorProps<Run>) => {
  // Newest-first (see useForecastDataQa / useScalingRuns), so option 0 is the latest.
  const options = useMemo(
    () =>
      runs.map((run, i) => ({
        value: runKey(run),
        label: i === 0 ? `${formatLabel(run)} (latest)` : formatLabel(run)
      })),
    [runs, runKey, formatLabel]
  );

  // Defensive: `runs[0]` below would throw on an empty list.
  if (runs.length === 0) {
    return null;
  }

  // One run: show its label rather than a one-option dropdown.
  const picker =
    runs.length < 2 ? (
      <span className='run-selector-label'>{formatLabel(runs[0])}</span>
    ) : (
      <Dropdown
        overlayPlacement={Placement.RIGHT}
        className='run-selector'
        dropdownMenuClassName='run-selector-menu'
        size={Size.SM}
        stretch={StretchMode.FIT}
        value={selectedKey}
        options={options}
        onChange={onSelectRun}
      />
    );

  // Group is the single root so all in-place styling scopes under it.
  return (
    <span className='run-selector-group'>
      {picker}
      {onRefresh && (
        <IconButton
          appearance={Appearance.EMPTY}
          loading={isRefreshing}
          onClick={onRefresh}
          aria-label='Refresh runs'
          title='Refresh runs'
        >
          <MdRefresh />
        </IconButton>
      )}
    </span>
  );
};
